var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { defaultBaseEHRNote } from './baseNoteSchema';
import { riskForHarmToOthers, riskForHarmToSelf, usedSubstance, } from 'constants/ehrNoteEnums';
var defaultEmptyNote = Object.freeze(__assign(__assign({}, defaultBaseEHRNote), { memberHistory: {
        behavioralHealthHistory: {
            previousProblems: [],
        },
        substanceUseAbuse: {
            substances: [
                {
                    name: usedSubstance.alcohol,
                    comments: '',
                },
                {
                    name: usedSubstance.nicotine,
                    comments: '',
                },
                {
                    name: usedSubstance.caffeine,
                    comments: '',
                },
                {
                    name: usedSubstance.cannabis,
                    comments: '',
                },
            ],
        },
    }, subjective: {
        presentingProblem: '',
        sessionFocus: '',
    }, objective: {}, assessment: {
        diagnoses: [],
    }, plan: {
        primaryTherapeuticApproaches: [],
    }, safetyPlan: {} }));
var initializeDefaultSubstancesIfMissing = function (note) {
    var _a, _b, _c, _d, _e;
    if (!note.memberHistory) {
        note.memberHistory = {};
    }
    if (!note.memberHistory.substanceUseAbuse) {
        note.memberHistory.substanceUseAbuse = {};
    }
    if (!note.memberHistory.substanceUseAbuse.substances ||
        ((_c = (_b = (_a = note.memberHistory) === null || _a === void 0 ? void 0 : _a.substanceUseAbuse) === null || _b === void 0 ? void 0 : _b.substances) === null || _c === void 0 ? void 0 : _c.length) === 0) {
        note.memberHistory.substanceUseAbuse.substances = (_e = (_d = defaultEmptyNote.memberHistory) === null || _d === void 0 ? void 0 : _d.substanceUseAbuse) === null || _e === void 0 ? void 0 : _e.substances;
    }
    return note;
};
/**
 * This processes a loaded note and makes any modifications we need to make
 * it ready for editing.  Right now those modifications are almost nothing,
 * However, when they saved no substances on substance use, we need to default
 * some again on the field array.
 */
var prepareExistingEHRNote = function (otherFields) {
    return __assign({}, initializeDefaultSubstancesIfMissing(otherFields));
};
export var stripOptionalEnums = function (obj) {
    var _a, _b, _c, _d, _e;
    if (!Array.isArray(obj.plan.primaryTherapeuticApproaches)) {
        delete obj.plan.primaryTherapeuticApproaches;
    }
    if (obj.plan.plannedFrequencyOfEngagementWithClient === '') {
        delete obj.plan.plannedFrequencyOfEngagementWithClient;
    }
    if (((_a = obj.assessment.riskConcerns) === null || _a === void 0 ? void 0 : _a.riskForHarmToSelf) === '') {
        delete obj.assessment.riskConcerns.riskForHarmToSelf;
    }
    if (((_b = obj.assessment.riskConcerns) === null || _b === void 0 ? void 0 : _b.riskForHarmToOthers) === '') {
        delete obj.assessment.riskConcerns.riskForHarmToOthers;
    }
    for (var _i = 0, _f = (_e = (_d = (_c = obj.memberHistory) === null || _c === void 0 ? void 0 : _c.substanceUseAbuse) === null || _d === void 0 ? void 0 : _d.substances) !== null && _e !== void 0 ? _e : []; _i < _f.length; _i++) {
        var substance = _f[_i];
        if (!substance.currentUse) {
            delete substance.currentUse;
        }
    }
    var id = obj.id, createdAt = obj.createdAt, updatedAt = obj.updatedAt, signedAt = obj.signedAt, otherFields = __rest(obj, ["id", "createdAt", "updatedAt", "signedAt"]);
    return __assign({}, otherFields);
};
export var ehrNoteSchemaInstance = {
    defaultValues: defaultEmptyNote,
    useDeprecatedValues: function () { return ({
        'assessment.riskConcerns.riskForHarmToOthers': [riskForHarmToOthers.notApplicable],
        'assessment.riskConcerns.riskForHarmToSelf': [riskForHarmToSelf.notApplicable],
    }); },
    useDeprecatedFields: function () { return [
        'memberHistory.behavioralHealthHistory.summaryOfPriorCare',
        'memberHistory.medicalHistory.physicalChallenges',
        'memberHistory.behavioralHealthHistory.psychiatricHospitalizations',
        'memberHistory.familyHistory.substanceUseAbuse',
        'memberHistory.socialDevelopmentalHistory.personalRelationships',
        'memberHistory.socialDevelopmentalHistory.familyRelationships',
        'memberHistory.socialDevelopmentalHistory.dependentCare',
        'memberHistory.socialDevelopmentalHistory.culturalLinguisticConsiderations',
        'memberHistory.socialDevelopmentalHistory.currentLivingArrangements',
        'memberHistory.socialDevelopmentalHistory.education',
        'memberHistory.socialDevelopmentalHistory.employmentHistoryAndReadiness',
        'memberHistory.socialDevelopmentalHistory.legalHistory',
        'memberHistory.socialDevelopmentalHistory.socialSupportSystem',
        'plan.homeworkForClient',
        'plan.additionalPlanNotes',
        'memberHistory.behavioralHealthHistory.therapyOutpatientTreatments',
        'memberHistory.medications.psychiatricMedications',
        'memberHistory.medications.otherMedications',
        'memberHistory.familyHistory.medicalHistory',
        'assessment.riskConcerns.elevatedRiskPlan.descriptionOfImmediateTreatmentPlan',
    ]; },
    prepareFormForSave: function (form) { return (__assign({}, stripOptionalEnums(form.getValues()))); },
    prepareExistingNote: prepareExistingEHRNote,
};
